import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './index.css';
import App from './App';
import Home from './pages/Home';
import DataQuadLanding from './pages/DataQuadLanding';
import PricingPage from './pages/PricingPage';
import DataQualityDetectiveLogin from './pages/DataQualityDetectiveLogin';
import ResetPassword from './pages/ResetPassword';
import Subscription from './pages/Subscription';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import VerifyPage from './pages/VerifyPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import RefundPage from './pages/RefundPage';
import Download from './pages/Download';
import AccountSettings from './pages/AccountSettings';
import Pulse from './pages/Pulse';
import EnumeratorApply from './pages/EnumeratorApply';
import { ThemeProvider, ThemeSwitcher } from './utils/ThemeSwitcher';
import './utils/dedupeFetch'; // dedupe concurrent heavy analysis requests
 
// Determine which site we're running based on environment variable
// REACT_APP_SITE_MODE: 'noblesyntax' or 'dataquad'
const SITE_MODE = process.env.REACT_APP_SITE_MODE || 'dataquad';
 
// Detect Tauri desktop app
const isTauri = Boolean(window.__TAURI_INTERNALS__);
 
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  if (!token) {
    if (SITE_MODE === 'noblesyntax') {
      window.location.href = 'https://dataquadapp.com/login';
      return null;
    }
    return <Navigate to="/login" />;
  }
  return children;
};
 
const ExternalRedirect = ({ to }) => {
  React.useEffect(() => {
    window.location.href = to;
  }, [to]);
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      fontSize: '16px',
      color: '#6B7280',
      fontFamily: "'Outfit', sans-serif",
    }}>
      Redirecting to DataQuad...
    </div>
  );
};
 
// ============================================================================
// NOBLE SYNTAX SITE (noblesyntax.com)
// Company website - LIGHT MODE ONLY, no ThemeSwitcher
// ============================================================================
const NobleSyntaxRouter = () => {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/careers/enumerators" element={<EnumeratorApply />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/refund" element={<RefundPage />} />
          <Route path="/download" element={<ExternalRedirect to="https://dataquadapp.com/download" />} />
          <Route path="/login" element={<ExternalRedirect to="https://dataquadapp.com/login" />} />
          <Route path="/reset-password" element={<ExternalRedirect to={`https://dataquadapp.com/reset-password${window.location.search}`} />} />
          <Route path="/app" element={<ExternalRedirect to="https://dataquadapp.com/app" />} />
          <Route path="/subscription" element={<ExternalRedirect to="https://dataquadapp.com/subscription" />} />
          <Route path="/pricing" element={<ExternalRedirect to="https://dataquadapp.com/pricing" />} />
          <Route path="/subscription/success" element={<ExternalRedirect to="https://dataquadapp.com/subscription/success" />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        {/* No ThemeSwitcher for Noble Syntax - light mode only */}
      </BrowserRouter>
    </ThemeProvider>
  );
};
 
// Route-aware theme switcher — hidden on pages that have their own footer toggle
const RouteAwareThemeSwitcher = () => {
  const location = useLocation();
  const hiddenPaths = ['/', '/pricing', '/login', '/reset-password', '/terms', '/privacy', '/refund', '/download', '/settings', '/dx'];
  const isHidden = hiddenPaths.includes(location.pathname);
  return (
    <div id="global-theme-toggle" style={isHidden ? { position: 'fixed', top: -9999, left: -9999, opacity: 0, pointerEvents: 'none' } : {}}>
      <ThemeSwitcher />
    </div>
  );
};
 
// ============================================================================
// DATAQUAD SITE (dataquadapp.com)
// The actual DataQuad application - has dark/light mode
// ============================================================================
const DataQuadRouter = () => {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <Routes>
          {/* Landing page for web, redirect to login for Tauri desktop */}
          <Route path="/" element={isTauri ? <Navigate to="/login" /> : <DataQuadLanding />} />
          <Route path="/login" element={<DataQualityDetectiveLogin />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/download" element={<Download />} />
          <Route path="/subscription" element={<Subscription />} />
          <Route path="/subscription/success" element={<SubscriptionSuccess />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/settings" element={
            <ProtectedRoute>
              <AccountSettings />
            </ProtectedRoute>
          } />
          <Route path="/verify/:sessionId" element={<VerifyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/refund" element={<RefundPage />} />
          <Route path="/dx" element={<Pulse />} />
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <App />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <ProtectedRoute>
                <App />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        <RouteAwareThemeSwitcher />
      </BrowserRouter>
    </ThemeProvider>
  );
};
 
const AppRouter = SITE_MODE === 'noblesyntax' ? NobleSyntaxRouter : DataQuadRouter;
 
const root = ReactDOM.createRoot(document.getElementById('root'));
// Fullscreen toggle with F11
document.addEventListener('keydown', (e) => {
  if (e.key === 'F11') {
    e.preventDefault();
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }
});
root.render(
  <React.StrictMode>
    <AppRouter />
  </React.StrictMode>
);